import { Reducer } from "react";

// LuckyDraw 게임 상태 관리 리듀서
// 1인용으로 서버 통신 없이 로컬에서 모든 처리

export type DrawMode = "number" | "prize";

export interface Prize {
  id: string;
  name: string;
  quantity: number;
  remaining: number; // 남은 수량
}

export interface DrawResult {
  id: string;
  timestamp: number;
  mode: DrawMode;
  result: string; // 숫자모드: "5", 경품모드: "상품명"
}

export interface LuckyDrawState {
  // 모드 설정
  mode: DrawMode; // "number" | "prize"

  // 숫자 모드 설정
  minNumber: number; // 최소 숫자
  maxNumber: number; // 최대 숫자
  drawnNumbers: number[]; // 이미 뽑힌 숫자들

  // 경품 모드 설정
  prizes: Prize[];

  // 공통 상태
  started: boolean;
  isDrawing: boolean; // 추첨 애니메이션 중

  // 결과
  drawHistory: DrawResult[];
  lastDraw: DrawResult | null;
}

export type LuckyDrawAction =
  | { type: "SET_MODE"; mode: DrawMode }
  | { type: "SET_MIN_NUMBER"; number: number }
  | { type: "SET_MAX_NUMBER"; number: number }
  | { type: "ADD_PRIZE"; name: string; quantity: number }
  | { type: "EDIT_PRIZE"; id: string; name: string; quantity: number }
  | { type: "DELETE_PRIZE"; id: string }
  | { type: "START_DRAW" }
  | { type: "START_DRAWING" } // 애니메이션 시작
  | { type: "STOP_DRAWING" } // 애니메이션 중지 및 결과 확정
  | { type: "COMPLETE_DRAW"; result: string } // 추첨 완료
  | { type: "RESET_NUMBERS" } // 숫자 모드에서 뽑힌 숫자 리셋
  | { type: "RESET_PRIZES" } // 경품 모드에서 모든 경품 수량 리셋
  | { type: "BACK_TO_SETTINGS" } // 설정으로 돌아가기
  | { type: "RESET_GAME" }; // 전체 게임 리셋

export const initialLuckyDrawState: LuckyDrawState = {
  mode: "number",
  minNumber: 1,
  maxNumber: 10,
  drawnNumbers: [],
  prizes: [],
  started: false,
  isDrawing: false,
  drawHistory: [],
  lastDraw: null,
};

// 숫자 모드에서 랜덤 숫자 뽑기
const drawRandomNumber = (
  minNumber: number,
  maxNumber: number,
  drawnNumbers: number[]
): number | null => {
  const availableNumbers = Array.from(
    { length: maxNumber - minNumber + 1 },
    (_, i) => i + minNumber
  ).filter((num) => !drawnNumbers.includes(num));

  if (availableNumbers.length === 0) {
    return null; // 모든 숫자가 뽑힘
  }

  const randomIndex = Math.floor(Math.random() * availableNumbers.length);
  return availableNumbers[randomIndex];
};

// 경품 모드에서 랜덤 경품 뽑기
const drawRandomPrize = (prizes: Prize[]): string | null => {
  const availablePrizes: string[] = [];

  prizes.forEach((prize) => {
    for (let i = 0; i < prize.remaining; i++) {
      availablePrizes.push(prize.name);
    }
  });

  if (availablePrizes.length === 0) {
    return null; // 모든 경품이 소진됨
  }

  const randomIndex = Math.floor(Math.random() * availablePrizes.length);
  return availablePrizes[randomIndex];
};

export const luckyDrawReducer: Reducer<LuckyDrawState, LuckyDrawAction> = (
  state,
  action
) => {
  switch (action.type) {
    case "SET_MODE":
      return {
        ...state,
        mode: action.mode,
        started: false,
        drawnNumbers: [],
        drawHistory: [],
        lastDraw: null,
      };

    case "SET_MIN_NUMBER":
      return {
        ...state,
        minNumber: Math.max(1, Math.min(state.maxNumber - 1, action.number)),
        drawnNumbers: [],
        started: false,
      };

    case "SET_MAX_NUMBER":
      return {
        ...state,
        maxNumber: Math.max(state.minNumber + 1, Math.min(9999, action.number)),
        drawnNumbers: [],
        started: false,
      };

    case "ADD_PRIZE":
      const newPrize: Prize = {
        id: Date.now().toString(),
        name: action.name,
        quantity: Math.max(1, action.quantity),
        remaining: Math.max(1, action.quantity),
      };
      return {
        ...state,
        prizes: [...state.prizes, newPrize],
      };

    case "EDIT_PRIZE":
      return {
        ...state,
        prizes: state.prizes.map((prize) =>
          prize.id === action.id
            ? {
                ...prize,
                name: action.name,
                quantity: Math.max(1, action.quantity),
                remaining: Math.max(1, action.quantity),
              }
            : prize
        ),
      };

    case "DELETE_PRIZE":
      return {
        ...state,
        prizes: state.prizes.filter((prize) => prize.id !== action.id),
      };

    case "START_DRAW":
      return {
        ...state,
        started: true,
      };

    case "START_DRAWING":
      return {
        ...state,
        isDrawing: true,
      };

    case "STOP_DRAWING":
      // 현재 애니메이션 중인 결과를 확정
      const currentResult = state.isDrawing ? state.lastDraw?.result || "" : "";
      if (currentResult) {
        const newDrawResult: DrawResult = {
          id: Date.now().toString(),
          timestamp: Date.now(),
          mode: state.mode,
          result: currentResult,
        };

        let updatedState = {
          ...state,
          isDrawing: false,
          lastDraw: newDrawResult,
          drawHistory: [newDrawResult, ...state.drawHistory],
        };

        // 모드별 후처리
        if (state.mode === "number") {
          const drawnNumber = parseInt(currentResult);
          updatedState.drawnNumbers = [...state.drawnNumbers, drawnNumber];
        } else if (state.mode === "prize") {
          // 경품 수량 감소
          updatedState.prizes = state.prizes.map((prize) =>
            prize.name === currentResult && prize.remaining > 0
              ? { ...prize, remaining: prize.remaining - 1 }
              : prize
          );
        }

        return updatedState;
      }
      return {
        ...state,
        isDrawing: false,
      };

    case "COMPLETE_DRAW":
      const newDrawResult: DrawResult = {
        id: Date.now().toString(),
        timestamp: Date.now(),
        mode: state.mode,
        result: action.result,
      };

      let updatedState = {
        ...state,
        isDrawing: false,
        lastDraw: newDrawResult,
        drawHistory: [newDrawResult, ...state.drawHistory],
      };

      // 모드별 후처리
      if (state.mode === "number") {
        const drawnNumber = parseInt(action.result);
        updatedState.drawnNumbers = [...state.drawnNumbers, drawnNumber];
      } else if (state.mode === "prize") {
        // 경품 수량 감소
        updatedState.prizes = state.prizes.map((prize) =>
          prize.name === action.result && prize.remaining > 0
            ? { ...prize, remaining: prize.remaining - 1 }
            : prize
        );
      }

      return updatedState;

    case "RESET_NUMBERS":
      return {
        ...state,
        drawnNumbers: [],
        drawHistory: [],
        lastDraw: null,
      };

    case "RESET_PRIZES":
      return {
        ...state,
        prizes: state.prizes.map((prize) => ({
          ...prize,
          remaining: prize.quantity,
        })),
        drawHistory: [],
        lastDraw: null,
      };

    case "BACK_TO_SETTINGS":
      return {
        ...state,
        started: false,
        isDrawing: false,
        drawnNumbers: [],
        drawHistory: [],
        lastDraw: null,
      };

    case "RESET_GAME":
      return {
        ...initialLuckyDrawState,
        mode: state.mode,
        minNumber: state.minNumber,
        maxNumber: state.maxNumber,
        prizes: state.prizes.map((prize) => ({
          ...prize,
          remaining: prize.quantity,
        })),
      };

    default:
      return state;
  }
};

// 유틸리티 함수들
export const canDraw = (state: LuckyDrawState): boolean => {
  if (!state.started || state.isDrawing) return false;

  if (state.mode === "number") {
    const totalNumbers = state.maxNumber - state.minNumber + 1;
    return state.drawnNumbers.length < totalNumbers;
  } else {
    return state.prizes.some((prize) => prize.remaining > 0);
  }
};

export const performDraw = (state: LuckyDrawState): string | null => {
  if (state.mode === "number") {
    const number = drawRandomNumber(
      state.minNumber,
      state.maxNumber,
      state.drawnNumbers
    );
    return number ? number.toString() : null;
  } else {
    return drawRandomPrize(state.prizes);
  }
};

export const getRemainingCount = (state: LuckyDrawState): number => {
  if (state.mode === "number") {
    const totalNumbers = state.maxNumber - state.minNumber + 1;
    return totalNumbers - state.drawnNumbers.length;
  } else {
    return state.prizes.reduce((sum, prize) => sum + prize.remaining, 0);
  }
};
